import { useRouter } from "@/lib/navigation";
import { formatDistanceToNow } from "date-fns";
import { ArrowRight, Bell, X } from "lucide-react";
import { Button, toast as sonnerToast } from "@myela/ui";
import type { UserAlert } from "@/hooks/useUserAlerts";
import { buildAlertLink } from "@/lib/alertLink";
import { IconTooltip } from "@/components/ui/icon-tooltip";

function NotificationToast({
  toastId,
  event,
}: {
  toastId: string | number;
  event: UserAlert;
}) {
  const router = useRouter();
  const link = buildAlertLink(event);
  const createdAt = new Date(event.created_date);
  const eyebrow = event.alert_type?.title ?? "Notification";

  const handleView = () => {
    if (link) router.push(link);
    sonnerToast.dismiss(toastId);
  };

  const handleDismiss = () => {
    sonnerToast.dismiss(toastId);
  };

  return (
    <div
      role="alert"
      className="pointer-events-auto relative w-full overflow-hidden rounded-md border bg-background shadow-lg"
    >
      {/* Left accent — keeps the toast distinct from action confirmations. */}
      <div className="absolute inset-y-0 left-0 w-1 bg-primary" aria-hidden />

      <div className="flex items-start gap-2 p-4">
        {/* Icon + pulsing dot to signal "new" */}
        <div className="relative shrink-0">
          <div className="flex size-9 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Bell className="h-4 w-4" />
          </div>
          <span
            className="absolute -right-0.5 -top-0.5 inline-flex size-2.5 items-center justify-center rounded-full bg-primary ring-2 ring-background"
            aria-hidden
          >
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-60" />
          </span>
        </div>

        <div className="min-w-0 flex-1">
          {/* Eyebrow makes it clear this is an INCOMING notification, not a
              confirmation of something the user just did. */}
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {eyebrow}
          </div>

          <div className="mt-1 line-clamp-2 text-sm font-medium leading-snug">
            {event.title}
          </div>
          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
            {event.message}
          </p>

          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {formatDistanceToNow(createdAt, { addSuffix: true })}
            </span>
            {link && (
              <Button variant="ghost" size="sm" onClick={handleView}>
                View
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <IconTooltip label="Dismiss notification" className="z-[1000000000]">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Dismiss notification"
            onClick={handleDismiss}
          >
            <X className="h-4 w-4" />
          </Button>
        </IconTooltip>
      </div>
    </div>
  );
}

// Public API — call this from useNotificationSocket when a `notification:new`
// event arrives. Keeps the rest of the toast system (action confirmations) on
// the plain `toast.info/success/error` channel so the two read distinctly.
//
// `unstyled: true` is required: without it sonner wraps the JSX in its own
// `[data-sonner-toast]` div with a fixed ~360px width and default border /
// background, which both clips the accent stripe and double-applies a card
// frame on top of ours. Per-toast `position` is intentionally omitted — the
// shared Toaster lives in Providers.tsx with `position="bottom-right"` and
// overriding it per-toast renders into a separate region that isn't always
// visible behind sidebars / modals.
export function showNotificationToast(event: UserAlert): void {
  // `style` overrides sonner's default --width: 356px (which clipped the
  // accent stripe + dismiss button) and removes its default padding /
  // background so our own card chrome is the only one rendered.
  sonnerToast.custom((id) => <NotificationToast toastId={id} event={event} />, {
    duration: 8000,
    unstyled: true,
    closeButton: false,
    style: {
      width: "380px",
      maxWidth: "calc(100vw - 2rem)",
      padding: 0,
      background: "transparent",
      border: "none",
      boxShadow: "none",
    },
  });
}
